import React, { useState } from "react";
import Popup from "../../../components/common/Popup";
import iconChecked from "../../../assets/checked.svg";
import iconUnCheck from "../../../assets/uncheck.svg";
import acknowledgement_title from "../../../assets/acknowledgement_title.png";
import { TeamAPI } from "../../../services/api";
import { COMPETITION_YEAR } from "../../../config/competition";

interface TeamMember {
  id: number;
  user_id: number;
  member_position: string;
  full_name: string;
  personal_email: string;
  terms_status?: string;
  terms_signed_at?: string;
}

interface Team {
  id: number;
  team_id: string;
  team_name: string;
  members: TeamMember[];
  acknowledgment_status: string;
  [key: string]: any;
}

interface SignTermsConditionsStatusProps {
  team: Team;
  onUpdate: () => void;
}

const SignTermsConditionsStatus: React.FC<SignTermsConditionsStatusProps> = ({
  team,
  onUpdate,
}) => {
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [loading, setLoading] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const currentMember = team.members.find((m) => m.user_id === currentUser.id);
  const otherMember = team.members.find((m) => m.user_id !== currentUser.id);

  // Check individual member signature status
  const userHasSigned = currentMember?.terms_status === "signed";
  const friendHasSigned = otherMember?.terms_status === "signed";

  const handleTermsAccepted = async () => {
    setLoading(true);
    try {
      // API call to sign terms & conditions
      const response = await TeamAPI.signTerms(team.team_id);
      console.log("Terms & Conditions signed:", response);

      setShowTermsPopup(false);
      onUpdate(); // Refresh team data to show updated status
    } catch (error: any) {
      console.error("Error signing terms:", error);
      alert(
        error.message || "Failed to sign Terms & Conditions. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex items-center space-x-2 mb-4">
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.3333 17.5V15.8333C13.3333 14.9493 12.9821 14.1014 12.3569 13.4763C11.7318 12.8512 10.884 12.5 9.99992 12.5H4.16658C3.28253 12.5 2.43468 12.8512 1.80956 13.4763C1.18444 14.1014 0.833252 14.9493 0.833252 15.8333V17.5M14.1666 9.16667L15.8333 10.8333L19.1666 7.5M10.4166 5.83333C10.4166 7.67428 8.9242 9.16667 7.08325 9.16667C5.2423 9.16667 3.74992 7.67428 3.74992 5.83333C3.74992 3.99238 5.2423 2.5 7.08325 2.5C8.9242 2.5 10.4166 3.99238 10.4166 5.83333Z"
            stroke="url(#paint0_linear_464_3997)"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <defs>
            <linearGradient
              id="paint0_linear_464_3997"
              x1="9.99992"
              y1="2.5"
              x2="9.99992"
              y2="17.5"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#403634" />
              <stop offset="1" stopColor="#1E1E1E" />
            </linearGradient>
          </defs>
        </svg>
        <h3 className="text-lg font-semibold text-gray-900">
          Sign Terms & Conditions
        </h3>
      </div>

      <div className="flex gap-6 mb-6">
        {/* Left Column: Two Users Side by Side */}
        <div className="flex-1">
          <div className="flex gap-3">
            {/* Current User */}
            <div className="flex-1 text-center">
              <div className="bg-white border border-gray-300 rounded-lg p-4 mb-2 h-full flex flex-col">
                <p className="text-sm font-semibold text-gray-900 mb-2">You</p>
                <div
                  className={`w-12 h-12 mx-auto mb-2 rounded-full flex items-center justify-center ${
                    userHasSigned ? "bg-green-100" : "bg-gray-100"
                  }`}
                >
                  {userHasSigned ? (
                    <img src={iconChecked} className="w-full" />
                  ) : (
                    <img src={iconUnCheck} className="w-full" />
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  {userHasSigned ? "Signed" : "Waiting"}
                </p>
                <div className="mt-1 h-4 flex items-center justify-center">
                  {userHasSigned && currentMember?.terms_signed_at ? (
                    <p className="text-xs text-gray-400">
                      {new Date(
                        currentMember.terms_signed_at,
                      ).toLocaleDateString()}
                    </p>
                  ) : (
                    <div className="h-4"></div>
                  )}
                </div>
              </div>
            </div>

            {/* Other Member */}
            <div className="flex-1 text-center">
              <div className="bg-white border border-gray-300 rounded-lg p-4 mb-2 h-full flex flex-col">
                <p className="text-sm font-semibold text-gray-900 mb-2">
                  {otherMember?.full_name || "Your friend"}
                </p>
                <div
                  className={`w-12 h-12 mx-auto mb-2 rounded-full flex items-center justify-center ${
                    friendHasSigned ? "bg-green-100" : "bg-gray-100"
                  }`}
                >
                  {friendHasSigned ? (
                    <img src={iconChecked} className="w-full" />
                  ) : (
                    <img src={iconUnCheck} className="w-full" />
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  {friendHasSigned ? "Signed" : "Waiting"}
                </p>
                <div className="mt-1 h-4 flex items-center justify-center">
                  {friendHasSigned && otherMember?.terms_signed_at ? (
                    <p className="text-xs text-gray-400">
                      {new Date(
                        otherMember.terms_signed_at,
                      ).toLocaleDateString()}
                    </p>
                  ) : (
                    <div className="h-4"></div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Instructions and Button */}
        <div className="flex-1 flex flex-col justify-center">
          <p className="text-sm text-gray-600 mb-4">
            Please click the button below to review and accept the Terms and
            Conditions.
          </p>

          <button
            onClick={() => setShowTermsPopup(true)}
            className="bg-gray-800 text-white py-3 px-4 rounded-lg hover:bg-gray-700 transition-colors font-medium"
            disabled={userHasSigned}
          >
            {userHasSigned ? "Terms Accepted" : "Acknowledgement"}
          </button>
        </div>
      </div>

      {/* Terms & Conditions Popup */}
      <Popup
        isOpen={showTermsPopup}
        onClose={() => !loading && setShowTermsPopup(false)}
        title="TERMS & CONDITIONS"
        mode="confirmation"
        onConfirm={handleTermsAccepted}
        confirmText={loading ? "Processing..." : "I understand and accept"}
        cancelText="Back"
      >
        <div className="space-y-4">
          <div className="w-full">
            <img src={acknowledgement_title} className="w-full" />
          </div>
          <p className="text-sm text-gray-600">
            With my full knowledge of the Vietnam Young Lions competition held
            by AIMACADEMY Training Joint Stock Company located at 146Bis Nguyen
            Van Thu Street, Tan Dinh Ward, Ho Chi Minh City, Vietnam{" "}
            <b>(“AIM Academy”)</b>, I represent and warrant that: (i) I
            voluntarily participate in the Vietnam Young Lions competition as
            individual who is independent with my former employer or current
            employer or current university or any other third party; and (ii)
            the information on this Registration Form is complete and accurate.
            I authorize <b>AIM Academy</b> to verify my information and use or
            transfer my information and images for public relations (PR)
            purposes. I understand that the provision of any false or misleading
            information or invalid documents will result in this application
            becoming invalid.
          </p>
          <p>
            I commit that my documents, files, presentation, ideas, creativity,
            innovations, suggestions, solutions or answers and the likes in the
            course of the Vietnam Young Lions (the <b>“Submission”</b>) are
            original, that the <b>Submission</b> has not been previously
            published, and that neither it nor its content(s) infringes upon or
            violates the rights of any third party, or any rights of privacy,
            publicity, copyrights, trademarks or other intellectual property
            rights. I shall defend, indemnify and hold <b>AIM Academy</b>, its
            officers, employees and volunteers harmless from any and all claims,
            injuries, damages, losses or suits including attorney fees, arising
            out of or in connection with the <b>Submission</b>.
          </p>
          <p>
            I acknowledge that I do not have, have never had, and shall not
            claim to have, any ownership, rights to register or other legal
            rights in the copyrights and related rights, inventions, utility
            solutions, designs, marks, know-how or other intellectual property
            relating to, or arising from the <b>Submission</b> (the{" "}
            <b>“Intellectual Property”</b>). I further acknowledge that the
            Intellectual Property shall be exclusively registered and owned by
            <b>AIM Academy</b> or any <b>Sponsor</b> which <b>AIM Academy</b>{" "}
            assigns. By execution of this Acknowledgment, I understand that I am
            waiving any and all rights I may have to any consideration, equity,
            monetary amounts, or royalty payments, which may be received by{" "}
            <b>AIM Academy</b> or any <b>Sponsor</b> which <b>AIM Academy</b>{" "}
            assigns, related to, or arising from the sale, assignment, license
            or other transfer or transaction involving some or all of the rights
            in the Intellectual Property.
          </p>
          <p>
            To the extent that the ownership of the Intellectual Property does
            not automatically vest in <b>AIM Academy</b>, I hereby grant and
            assign to
            <b>AIM Academy</b> or any <b>Sponsor</b> which <b>AIM Academy</b>{" "}
            assigns all rights and interest in and to the Intellectual Property
            including but not limited to the ownership, rights to register and
            agree to execute all documents reasonably requested by{" "}
            <b>AIM Academy</b>
            or any <b>Sponsor</b> which <b>AIM Academy</b> assigns to effectuate
            the foregoing.
          </p>
          <p>
            I further acknowledge that the winner(s) will be selected by juries
            according to the winner selection process. The juries’ decisions
            shall be final, not be questioned and no correspondence shall be
            entertained.
          </p>
          <p>
            I hereby declare that I have read the <b>Acknowledgement</b> and{" "}
            <b>
              <a
                href="https://vietnamyounglions.net/terms-and-conditions/"
                target="_blank"
              >
                Terms and Conditions
              </a>
            </b>{" "}
            carefully, that I have been afforded sufficient time to understand
            the terms and effects of this <b>Acknowledgement</b> and the listed
            items in the <b>Terms and Conditions</b>. I am freely and
            voluntarily following the <b>Terms and Conditions</b> and executing
            this <b>Acknowledgement</b> and that neither AIM Academy nor any
            <b>Sponsor</b> nor any AIM Academy’s officers, directors, employees
            or representatives have made any representations inconsistent with
            the terms and effects of this <b>Acknowledgement</b>.
          </p>
        </div>
      </Popup>
    </div>
  );
};

export default SignTermsConditionsStatus;
